import { useCallback, useEffect, useRef, useState } from "react";

export default function useInteract3D(maxTilt = 14, enabled = true) {
  const tiltRef = useRef(null);
  const tiltState = useRef({ x: 0, y: 0 });
  const [popping, setPopping] = useState(false);
  const popLockRef = useRef(false);

  const applyTilt = useCallback(() => {
    const el = tiltRef.current;
    if (!el || popping || !enabled) return;

    const { x, y } = tiltState.current;
    el.style.transform = `
      perspective(900px)
      rotateX(${-y * maxTilt}deg)
      rotateY(${x * maxTilt}deg)
      translateZ(12px)
    `;
  }, [enabled, maxTilt, popping]);

  const resetTilt = useCallback(() => {
    const el = tiltRef.current;
    if (!el || popping || !enabled) return;
    tiltState.current = { x: 0, y: 0 };
    el.style.transform =
      "perspective(900px) rotateX(0deg) rotateY(0deg) translateZ(0)";
  }, [enabled, popping]);

  const triggerPop = useCallback(() => {
    const el = tiltRef.current;
    if (!el || !enabled || popLockRef.current) return;

    popLockRef.current = true;
    setPopping(true);
    el.style.transform =
      "perspective(900px) rotateX(-8deg) rotateY(16deg) scale(1.06) translateZ(24px)";

    window.setTimeout(() => {
      setPopping(false);
      popLockRef.current = false;
      applyTilt();
    }, 650);
  }, [applyTilt, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const el = tiltRef.current;
    if (!el) return;

    const updateFromPoint = (clientX, clientY) => {
      const rect = el.getBoundingClientRect();
      tiltState.current = {
        x: (clientX - rect.left) / rect.width - 0.5,
        y: (clientY - rect.top) / rect.height - 0.5,
      };
      applyTilt();
    };

    const onMove = (e) => updateFromPoint(e.clientX, e.clientY);

    const onTouchMove = (e) => {
      if (!e.touches[0]) return;
      updateFromPoint(e.touches[0].clientX, e.touches[0].clientY);
    };

    let touchStart = null;
    let touchMoved = false;

    const onTouchStart = (e) => {
      if (!e.touches[0]) return;
      touchMoved = false;
      touchStart = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    };

    const onTouchEnd = (e) => {
      if (!touchStart || !e.changedTouches[0] || touchMoved) {
        touchStart = null;
        return;
      }
      const dx = Math.abs(e.changedTouches[0].clientX - touchStart.x);
      const dy = Math.abs(e.changedTouches[0].clientY - touchStart.y);
      touchStart = null;
      if (dx < 12 && dy < 12) triggerPop();
    };

    const onTouchMoveStart = (e) => {
      if (!touchStart || !e.touches[0]) return;
      const dx = Math.abs(e.touches[0].clientX - touchStart.x);
      const dy = Math.abs(e.touches[0].clientY - touchStart.y);
      if (dx > 8 || dy > 8) touchMoved = true;
      onTouchMove(e);
    };

    const onClick = (e) => {
      if (e.pointerType === "touch") return;
      triggerPop();
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", resetTilt);
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMoveStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("click", onClick);

    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", resetTilt);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMoveStart);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("click", onClick);
    };
  }, [applyTilt, enabled, resetTilt, triggerPop]);

  return { tiltRef, popping };
}
